<template>
  <div class="p-4 pb-20">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h2 class="text-2xl font-bold text-gray-900">{{ t('marketplace.title') }}</h2>
        <p class="text-sm text-gray-600 mt-1">Connect with drone pilots nationwide</p>
      </div>
      <button class="agri-btn-primary flex items-center space-x-2">
        <AgriIcon name="plus" class="w-4 h-4" />
        <span>{{ t('marketplace.post_job') }}</span>
      </button>
    </div>

    <!-- Service Type Tabs -->
    <div class="agri-tabs mb-6">
      <button 
        v-for="service in serviceTypes" 
        :key="service.key"
        @click="activeService = service.key"
        :class="{ 'active': activeService === service.key }"
        class="agri-tab"
      >
        {{ service.label }}
      </button>
    </div>

    <!-- Service Requests -->
    <div class="mb-8">
      <h3 class="text-lg font-semibold mb-4 flex items-center space-x-2">
        <AgriIcon name="tasks" class="w-5 h-5 text-green-600" />
        <span>{{ t('marketplace.service_requests') }}</span>
      </h3>
      <div class="space-y-4">
        <div v-for="request in serviceRequests" :key="request.id" class="agri-card">
          <div class="p-6">
            <div class="flex justify-between items-start mb-4">
              <div class="flex-1">
                <h4 class="font-semibold text-gray-900 text-lg mb-1">{{ request.farmerName }}</h4>
                <div class="flex items-center space-x-2 text-sm text-gray-600 mb-2">
                  <AgriIcon name="location" class="w-4 h-4" />
                  <span>{{ request.farmLocation }}</span>
                </div>
                <p class="text-gray-700 mb-3">{{ request.jobDescription }}</p>
              </div>
              <div class="text-right">
                <div class="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium mb-2">
                  {{ formatLandArea(request.area, request.areaUnit, currentLang) }}
                </div>
                <div class="text-lg font-bold text-green-600">฿{{ request.pricePerRai }}</div>
                <div class="text-xs text-gray-500">{{ t('marketplace.price_per_rai') }}</div>
              </div>
            </div>
            
            <div class="flex justify-between items-center">
              <div class="flex items-center space-x-2">
                <span class="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                  {{ t(`marketplace.${request.serviceType}`) }}
                </span>
                <span class="text-sm text-gray-500">{{ request.createdAt }}</span>
              </div>
              <button class="agri-btn-primary flex items-center space-x-2">
                <AgriIcon name="user" class="w-4 h-4" />
                <span>{{ t('marketplace.accept_job') }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Available Pilots -->
    <div>
      <h3 class="text-lg font-semibold mb-4 flex items-center space-x-2">
        <AgriIcon name="devices" class="w-5 h-5 text-blue-600" />
        <span>{{ t('marketplace.available_pilots') }}</span>
      </h3>
      <div class="space-y-4">
        <div v-for="pilot in availablePilots" :key="pilot.id" class="agri-card">
          <div class="p-6">
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center space-x-4">
                <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                  {{ pilot.name.charAt(0) }}
                </div>
                <div>
                  <h4 class="font-semibold text-gray-900 text-lg">{{ pilot.name }}</h4>
                  <p class="text-sm text-gray-600">{{ pilot.experience }} {{ t('account.pilot') }}</p>
                  <div class="flex items-center space-x-2 mt-1">
                    <div class="flex items-center">
                      <AgriIcon name="star" class="w-4 h-4 text-yellow-500" />
                      <span class="text-sm font-medium">{{ pilot.rating }}</span>
                    </div>
                    <span class="text-sm text-gray-500">•</span>
                    <span class="text-sm text-gray-600">{{ pilot.completedJobs }} {{ t('tasks.completed') }}</span>
                  </div>
                </div>
              </div>
              <div class="text-right">
                <div class="text-lg font-bold text-green-600">฿{{ pilot.pricePerRai }}-{{ pilot.maxPricePerRai }}</div>
                <div class="text-xs text-gray-500">{{ t('marketplace.price_per_rai') }}</div>
                <div class="text-xs text-gray-600 mt-1">{{ pilot.location }}</div>
              </div>
            </div>
            
            <div class="flex flex-wrap gap-2 mb-4">
              <span v-for="service in pilot.services" :key="service" 
                    class="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                {{ t(`marketplace.${service}`) }}
              </span>
            </div>
            
            <div class="flex justify-between items-center">
              <div class="flex items-center space-x-4 text-sm text-gray-600">
                <div class="flex items-center space-x-1">
                  <AgriIcon name="check" class="w-4 h-4 text-green-500" />
                  <span>Verified</span>
                </div>
                <div class="flex items-center space-x-1">
                  <AgriIcon name="clock" class="w-4 h-4 text-blue-500" />
                  <span>Quick Response</span>
                </div>
              </div>
              <button class="agri-btn-secondary flex items-center space-x-2">
                <AgriIcon name="message" class="w-4 h-4" />
                <span>{{ t('marketplace.contact_pilot') }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Floating Action Button -->
    <button class="agri-fab fixed bottom-20 right-4 z-30">
      <AgriIcon name="plus" class="w-6 h-6" />
    </button>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from '../composables/useI18n.js'
import { useLandUnits } from '../composables/useLandUnits.js'
import AgriIcon from '../components/AgriIcon.vue'

const { t, currentLang } = useI18n()
const { formatLandArea } = useLandUnits()

const activeService = ref('all')

const serviceTypes = [
  { key: 'all', label: t('common.all') || 'All' },
  { key: 'spraying', label: t('marketplace.spraying') },
  { key: 'seeding', label: t('marketplace.seeding') },
  { key: 'surveying', label: t('marketplace.surveying') }
]

const serviceRequests = ref([
  {
    id: 1,
    farmerName: 'สมชาย ใจดี',
    farmLocation: 'อำเภอบางปะอิน จังหวัดพระนครศรีอยุธยา',
    area: 50,
    areaUnit: 'rai',
    jobDescription: 'ต้องการพ่นยากำจัดศัตรูพืชในนาข้าว พื้นที่โล่ง ไม่มีสิ่งกีดขวาง',
    pricePerRai: 120,
    serviceType: 'spraying',
    createdAt: '2 ชั่วโมงที่แล้ว'
  },
  {
    id: 2,
    farmerName: 'มานี มีสุข',
    farmLocation: 'อำเภอหนองแค จังหวัดสระบุรี',
    area: 30,
    areaUnit: 'rai',
    jobDescription: 'หว่านปุ๋ยในสวนอ้อย ต้องการความแม่นยำสูง',
    pricePerRai: 80,
    serviceType: 'seeding',
    createdAt: '5 ชั่วโมงที่แล้ว'
  },
  {
    id: 3,
    farmerName: 'ประสิทธิ์ รุ่งเรือง',
    farmLocation: 'อำเภอเมือง จังหวัดนครราชสีมา',
    area: 100,
    areaUnit: 'rai',
    jobDescription: 'สำรวจความเสียหายของพืชผลหลังพายุ',
    pricePerRai: 60,
    serviceType: 'surveying',
    createdAt: '1 วันที่แล้ว'
  }
])

const availablePilots = ref([
  {
    id: 1,
    name: 'วีระชัย นักบิน',
    experience: '5 ปี',
    completedJobs: 156,
    rating: 4.8,
    services: ['spraying', 'seeding'],
    pricePerRai: 100,
    maxPricePerRai: 150,
    location: 'จังหวัดพระนครศรีอยุธยา'
  },
  {
    id: 2,
    name: 'สมศรี บินได้',
    experience: '3 ปี',
    completedJobs: 89,
    rating: 4.6,
    services: ['spraying', 'surveying'],
    pricePerRai: 80,
    maxPricePerRai: 120,
    location: 'จังหวัดสระบุรี'
  },
  {
    id: 3,
    name: 'บุญมี ทักษะดี',
    experience: '7 ปี',
    completedJobs: 234,
    rating: 4.9,
    services: ['spraying', 'seeding', 'surveying'],
    pricePerRai: 120,
    maxPricePerRai: 180,
    location: 'จังหวัดนครราชสีมา'
  }
])
</script>